import * as React from 'react';
import { PermissionEnum } from '@Application/Security/Roles';
import type { TemplateAssignmentScope } from '@Application/Documents/TemplateAssignments/TemplateAssignmentScope';
import TemplateAssignmentCard from '@UI/Pages/DocumentTemplates/Shared/TemplateAssignmentCard';
import ArchivedDocumentTypeCard from '@UI/Pages/DocumentTemplates/OrganizationDocumentTemplates/Sections/TemplateAssignements/ArchivedDocumentTypeCard';
import DocumentTypeActions from '@UI/Pages/DocumentTemplates/OrganizationDocumentTemplates/Sections/TemplateAssignements/DocumentTypeActions';
import TemplateAssignmentOverridesList from '@UI/Pages/DocumentTemplates/OrganizationDocumentTemplates/Sections/TemplateAssignements/TemplateAssignmentOverridesList';
import useHasPermission from '@UI/Providers/PermissionProvider/useHasPermission';
import type DocumentTemplatesListView from '@UI/Store/DocumentTemplates/Views/Document/DocumentTemplatesListView';
import type DocumentTypesListView from '@UI/Store/DocumentTypes/Views/DocumentTypesListView';
import type FacilitiesListView from '@UI/Store/Facilities/Views/FacilitiesListView';
import type FacilityTemplateOverridesListView from '@UI/Store/DocumentTemplates/Views/TemplateAssignements/FacilityTemplateOverridesListView';
import type TemplateAssignmentsListView from '@UI/Store/DocumentTemplates/Views/TemplateAssignements/TemplateAssignmentsListView';
import { css } from '@emotion/react';
import { Grid } from '@verclam/grid-system';

const sectionStyle = css`
	display: flex;
	flex-direction: column;
	gap: 1.25rem;
`;

type Props = {
	readonly organizationId: number;
	readonly assignments: TemplateAssignmentsListView;
	readonly templates: DocumentTemplatesListView;
	readonly documentTypes: DocumentTypesListView;
	readonly facilityOverrides: FacilityTemplateOverridesListView;
	readonly facilities: FacilitiesListView | null;
};

/**
 * "Affectation par type": one `TemplateAssignmentCard` per non-archived document type of the
 * account (the server answers with one entry per active type, assigned or not). Each card carries
 * the organization-only list of facility overrides for its type as `children` — the facility screen
 * never passes this — and, next to its title, `DocumentTypeActions` (rename/archive a free type,
 * gated on `DOCUMENT_TYPE_WRITE`, `null` for a system type). Archived types are grayed cards in the
 * very same `Grid`, after the active ones — never a separate section — shown only alongside
 * `DOCUMENT_TYPE_WRITE`: someone without it never sees archived types.
 */
const TemplateAssignmentsSection: React.FC<Props> = ({
	organizationId,
	assignments,
	templates,
	documentTypes,
	facilityOverrides,
	facilities,
}) => {
	const hasDocumentTypeWritePermission = useHasPermission(PermissionEnum.DOCUMENT_TYPE_WRITE);

	const scope = React.useMemo<TemplateAssignmentScope>(
		() => ({ type: 'organization', id: organizationId }),
		[organizationId],
	);

	const archivedTypes = documentTypes.archivedTypes();

	return (
		<section css={sectionStyle}>
			<Grid columns={2} columnGap={1.25} rowGap={1.25}>
				{assignments.list.map((assignment) => (
					<TemplateAssignmentCard
						key={assignment.documentTypeId}
						assignment={assignment}
						scope={scope}
						templates={templates}
						documentTypes={documentTypes}
						typeActions={
							<DocumentTypeActions
								documentTypeId={assignment.documentTypeId}
								documentTypes={documentTypes}
							/>
						}
					>
						<TemplateAssignmentOverridesList
							facilityOverridesList={facilityOverrides}
							templates={templates}
							facilities={facilities}
							organizationId={organizationId}
							documentTypeId={assignment.documentTypeId}
						/>
					</TemplateAssignmentCard>
				))}
				{hasDocumentTypeWritePermission
					? archivedTypes.map((documentType) => (
							<ArchivedDocumentTypeCard key={documentType.id} documentType={documentType} />
						))
					: null}
			</Grid>
		</section>
	);
};

export default TemplateAssignmentsSection;
